import * as React from 'react';
import {
  isModalAction,
  isModalTarget,
  type ModalAction,
} from '@packages/systems/overlays';
import {
  Note,
  SegmentedControl,
  SegmentedControlButton,
} from '@packages/systems/spring';
import {useExpressionEditorContext} from '@packages/systems/ExpressionEditor-utils';
import {SectionSelectorPopover} from '@packages/systems/designer/section-select';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {emotionStyled} from '@packages/utilities/styled';
import {Label} from './LinkUIComponents';
import {getScopedOverlayTargetComponent} from './LinkOverlayTargets';
import {
  selectOverlayContext,
  useOverlayTargetCreation,
  useGetContainingOverlayTarget,
  useOverlayTargetOptions,
  useOverlayTargetComponent,
} from './LinkOverlayTargetHooks';
import type {State} from './LinkTypes';

const FullWidthNote = emotionStyled(Note)({gridColumn: '1 / -1'});

const MISSING_MODAL_LABEL = 'Missing modal';

const getModalAction = (action: State['modalAction']): ModalAction =>
  isModalAction(action) ? action : 'show';

/**
 * Returns the Modal fields to start Modal mode with. Inside a Modal the
 * trigger can address, the link closes that Modal by default.
 */
export const useGetInitialModalState = (): (() => Pick<
  State,
  'modalTarget' | 'modalTargetComponent' | 'modalAction'
> | null) => {
  const getContainingTarget = useGetContainingOverlayTarget(isModalTarget);

  return React.useCallback(() => {
    const target = getContainingTarget();
    return target
      ? {
          modalTarget: target.targetId,
          modalTargetComponent: target.targetComponent,
          modalAction: 'hide',
        }
      : null;
  }, [getContainingTarget]);
};

export const LinkModalMode = ({
  state,
  dispatch,
  isOverride,
}: {
  state: State;
  dispatch: (arg1?: any) => void;
  isOverride: boolean;
}) => {
  const selectedId = state.modalTarget;
  const isEditingDefault = useExpressionEditorContext() === 'field-editor';
  const currentTargetComponent = useOverlayTargetComponent();
  const overlayContext = useDesignerSelector(selectOverlayContext);
  const {render, trigger, isCmsRichTextScope, targetCandidates} =
    overlayContext;
  const modalTargetComponent = getScopedOverlayTargetComponent(
    currentTargetComponent,
    isCmsRichTextScope
  );
  const onCreate = useOverlayTargetCreation(
    'modal',
    modalTargetComponent,
    overlayContext,
    isOverride
  );
  const {options, hasValidOptions} = useOverlayTargetOptions({
    isTarget: isModalTarget,
    missingLabel: MISSING_MODAL_LABEL,
    selectedId,
    render,
    selectedElement: trigger,
    isEditingDefault,
    candidates: targetCandidates,
  });
  const onActionChange = React.useCallback(
    (action: string) => {
      if (!isModalAction(action)) {
        return;
      }

      dispatch({
        payload: {...state, modalAction: action},
      });
    },
    [dispatch, state]
  );

  const onChange = React.useCallback(
    (value: string | null) => {
      dispatch({
        payload: {
          ...state,
          modalTarget: value ?? undefined,
          modalTargetComponent,
        },
      });
    },
    [dispatch, modalTargetComponent, state]
  );

  return (
    <>
      {!isEditingDefault || selectedId ? (
        <>
          <Label
            data-automation-id="symbols-dataType-modalTarget"
            isOverride={isOverride}
          >
            Target
          </Label>
          <SectionSelectorPopover
            value={selectedId}
            onSectionChange={onChange}
            options={options}
            placeholder="Choose a target..."
            searchPlaceholder="Search targets"
            emptyStateLabel="No modals found"
            hasValidOptions={hasValidOptions}
            emptyAction={
              onCreate
                ? {
                    label: 'Create modal',
                    onClick: onCreate,
                  }
                : undefined
            }
          />
        </>
      ) : null}
      {isEditingDefault ? (
        <FullWidthNote colorVariant="default" hasIcon={false}>
          Set the modal target on the component instance.
        </FullWidthNote>
      ) : null}
      <Label
        data-automation-id="symbols-dataType-modalAction"
        isOverride={isOverride}
      >
        Action
      </Label>
      <SegmentedControl
        data-automation-id="Type--Plugin_Enum_ModalAction_buttons"
        onChange={onActionChange}
        value={getModalAction(state.modalAction)}
      >
        <SegmentedControlButton
          data-automation-id="Type--Plugin_Enum_ModalAction_button-show"
          value="show"
        >
          Open
        </SegmentedControlButton>
        <SegmentedControlButton
          data-automation-id="Type--Plugin_Enum_ModalAction_button-hide"
          value="hide"
        >
          Close
        </SegmentedControlButton>
      </SegmentedControl>
    </>
  );
};
